import { cn } from "@lib/cn";
import { useAppSelector } from "@store";
import { showAnalyticsPlaceholder } from "@store/account/selectors";
import { IconClock } from "@tabler/icons-react";
import ReactECharts from "echarts-for-react";
import moment from "moment";
import { useMemo, useRef } from "react";
import { useAnalyticsStoreShallow } from "../store/analytics.store";
import type { DataByDate } from "../types";
import { getLineChartOptions, getSeriesColor } from "../utils";
import { EmptyStateMessage } from "./empty-state-message";
import { ErrorStateMessage } from "./error-state-message";

type ChartSeries = {
  name: string;
  key: string;
  color: string;
  data: number[];
};

const ReadersByTimeChart = ({
  data,
  isError,
  active,
  isEmpty,
  className,
}: {
  data: DataByDate[];
  isError: boolean;
  isEmpty: boolean;
  active: "readers" | "sessions";
  className?: string;
}) => {
  const showPlaceholder = useAppSelector(showAnalyticsPlaceholder);
  const chartRef = useRef(null);
  const { dateFrom, dateTo } = useAnalyticsStoreShallow((s) => ({
    dateFrom: s.date.dateFrom,
    dateTo: s.date.dateTo,
  }));

  const dates = useMemo(() => {
    // if (showPlaceholder) {
    //   const arr = [];
    //   const currentDate = moment();
    //   const endDate = moment().subtract(1, "day");
    //   while (currentDate.isSameOrBefore(endDate)) {
    //     arr.push(currentDate.format("MMM D"));
    //     currentDate.add(1, "day");
    //   }
    //   return arr;
    // }

    const arr = [];
    const currentDate = moment(dateFrom);
    const endDate = moment(dateTo).subtract(1, "day");
    while (currentDate.isSameOrBefore(endDate)) {
      arr.push(currentDate.format("MMM D"));
      currentDate.add(1, "day");
    }
    return arr;
  }, [dateFrom, dateTo, showPlaceholder]);

  const seriesList = useMemo(() => {
    if (!data?.length) return [];

    const dataByTitle = new Map<string, Map<string, number>>();
    const titles = new Set<string>();

    data.forEach((d) => {
      const dateKey = moment(d.date).format("MMM D");
      const title = d.story_title;

      if (!dataByTitle.has(title)) {
        dataByTitle.set(title, new Map());
      }
      dataByTitle.get(title)!.set(dateKey, d.data);
      titles.add(title);
    });

    const titlesArray = Array.from(titles);
    return titlesArray.map((title, index) => {
      const titleData = dataByTitle.get(title)!;
      const values = dates.map((date) => titleData.get(date) ?? 0);

      return {
        name: title,
        key: title,
        color: getSeriesColor(index),
        data: values,
      };
    }) as ChartSeries[];
  }, [dates, data]);

  const option = useMemo(() => {
    if (!seriesList.length) return getLineChartOptions([], dates, true);
    return getLineChartOptions(seriesList, dates);
  }, [seriesList, dates]);

  return (
    <div
      className={cn(
        "tw-relative tw-flex tw-flex-col tw-rounded-lg tw-outline tw-outline-1 tw-outline-blue-100 tw-bg-white",
        className,
      )}
    >
      <span className="tw-relative tw-z-[51] tw-flex tw-items-center tw-px-5 tw-pt-5 tw-font-sans tw-text-sm tw-font-semibold tw-text-blue">
        <IconClock className="tw-size-4 tw-text-blue tw-mr-2" />{" "}
        {active === "readers" ? "Readers" : "Sessions"} by time
      </span>
      {isError && (
        <div className="tw-absolute tw-inset-0 tw-z-[50] tw-flex tw-rounded-lg tw-items-center tw-justify-center tw-bg-white/80">
          <ErrorStateMessage />
        </div>
      )}
      {isEmpty && (
        <div className="tw-absolute tw-inset-0 tw-z-[50] tw-flex tw-rounded-lg tw-items-center tw-justify-center tw-bg-white/80">
          <EmptyStateMessage
            title="No analytics yet, but you're on the right track"
            description="Share your Storydocs to start collecting insights. We’ll show your data as soon as it's ready."
            className="tw-w-full tw-max-w-80"
          />
        </div>
      )}
      <div className="tw-grow tw-py-5 tw-px-4">
        <ReactECharts
          ref={chartRef}
          option={option}
          style={{ height: "100%", width: "100%" }}
          opts={{ renderer: "canvas" }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </div>
  );
};

export default ReadersByTimeChart;
